import {
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { type FC, type FormEvent, useState } from "react";

import { FilterMenuTitle } from "~/components/UI/FilterMenuTitle";
import { InputRange } from "~/components/UI/FilterRange/components/InputRange";
import { inputRangeSx } from "~/components/UI/FilterRange/components/InputRange/InputRange.styles";
import {
  Content,
  Section,
} from "~/feature/dashboard/components/sortAndFilters/StatusFilterDropdown/StatusFilterDropdown.styles";
import { EndsInCustomTypeFilterEnum, EndsInUnitEnum } from "~/types/endsIn";
import {
  describeEndsInCustom,
  type EndsInCustomFields,
  hasEndsInBetweenOrderError,
  labelByEndsInCustomType,
  pluralEndsInUnitLabel,
} from "~/utils/filters/endsInFilter";

interface IEndsInCustomDraft {
  endsInCustomType: EndsInCustomTypeFilterEnum;
  endsInValue1?: number;
  endsInUnit1: EndsInUnitEnum;
  endsInValue2?: number;
  endsInUnit2: EndsInUnitEnum;
}

const toDraft = (value?: EndsInCustomFields): IEndsInCustomDraft => ({
  endsInCustomType:
    value?.endsInCustomType ?? EndsInCustomTypeFilterEnum.LESS_THAN,
  endsInValue1: value?.endsInValue1,
  endsInUnit1: value?.endsInUnit1 ?? EndsInUnitEnum.HOUR,
  endsInValue2: value?.endsInValue2,
  endsInUnit2: value?.endsInUnit2 ?? EndsInUnitEnum.DAY,
});

const unitSelect = (
  unit: EndsInUnitEnum,
  onChange: (unit: EndsInUnitEnum) => void
) => (
  <InputAdornment position="end">
    <Select<EndsInUnitEnum>
      variant="standard"
      input={<InputBase />}
      value={unit}
      onChange={(event) => onChange(event.target.value)}
    >
      {Object.values(EndsInUnitEnum).map((option) => (
        <MenuItem key={option} value={option}>
          {pluralEndsInUnitLabel(option)}
        </MenuItem>
      ))}
    </Select>
  </InputAdornment>
);

interface EndsInCustomFormProps {
  initialValue?: EndsInCustomFields;
  onApply: (value: EndsInCustomFields) => void;
  onCancel: () => void;
}

export const EndsInCustomForm: FC<EndsInCustomFormProps> = ({
  initialValue,
  onApply,
  onCancel,
}) => {
  const [draft, setDraft] = useState(() => toDraft(initialValue));
  const isBetween =
    draft.endsInCustomType === EndsInCustomTypeFilterEnum.BETWEEN;
  const phrase = describeEndsInCustom(draft);
  const hasOrderError = hasEndsInBetweenOrderError(draft);
  const canApply = Boolean(phrase) && !hasOrderError;

  const patchDraft = (patch: Partial<IEndsInCustomDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    if (!canApply) {
      return;
    }

    onApply(
      isBetween
        ? draft
        : { ...draft, endsInValue2: undefined, endsInUnit2: undefined }
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Content>
        <FilterMenuTitle>Ends in</FilterMenuTitle>

        <Section>
          <FormControl size="small" sx={inputRangeSx}>
            <Select<EndsInCustomTypeFilterEnum>
              value={draft.endsInCustomType}
              onChange={(event) =>
                patchDraft({ endsInCustomType: event.target.value })
              }
            >
              {Object.values(EndsInCustomTypeFilterEnum).map((type) => (
                <MenuItem key={type} value={type}>
                  {labelByEndsInCustomType[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <InputRange
            value={draft.endsInValue1 ?? null}
            onChange={(value) =>
              patchDraft({ endsInValue1: value ?? undefined })
            }
            placeholder=""
            startAdornment={null}
            endAdornment={unitSelect(draft.endsInUnit1, (endsInUnit1) =>
              patchDraft({ endsInUnit1 })
            )}
          />

          {isBetween && (
            <>
              <Divider>
                <Typography variant="caption" color="textSecondary">
                  and
                </Typography>
              </Divider>
              <InputRange
                value={draft.endsInValue2 ?? null}
                onChange={(value) =>
                  patchDraft({ endsInValue2: value ?? undefined })
                }
                placeholder=""
                startAdornment={null}
                endAdornment={unitSelect(draft.endsInUnit2, (endsInUnit2) =>
                  patchDraft({ endsInUnit2 })
                )}
              />
            </>
          )}
        </Section>

        <Section>
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              type="submit"
              fullWidth
              disabled={!canApply}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Stack>

          {hasOrderError ? (
            <Typography variant="caption" color="error">
              The first value must be less than the second
            </Typography>
          ) : (
            phrase && (
              <Typography variant="caption" color="textSecondary">
                Show markets that end{isBetween ? " " : " in "}
                <b>{phrase}</b>
              </Typography>
            )
          )}
        </Section>
      </Content>
    </form>
  );
};
